import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ListChecks, FileText, Search, Loader2, History } from 'lucide-react';
import { api } from '../services/api';
import { withWorkEngagement } from './timer/workLogEngagement';
import { useAuthStore } from '../store/authStore';
import type { Ticket } from '../types';
import type { WorkLogDraftInitial } from '../types/voiceProposal';
import { toastDebtWarning } from '../utils/debtWarning';
import { toastWorkLogNotices } from '../utils/workLogNotices';
import ConsumptionPreview, { type ConsumptionChoice } from './ConsumptionPreview';
import Dialog from './ui/Dialog';
import Tabs from './ui/Tabs';
import StateBlock from './ui/StateBlock';
import { useIsDesktop } from './ui/useOverlay';
import Field, { FormErrorSummary, rules, useFormValidation, type FieldRule, type FormSchema } from './ui/Field';
import {
  DURATION_PRESETS, formatDurationInput, minutesBetween, parseDurationInput, startFromDuration, toLocalInputValue,
} from './worklogs/durationModel';
import { useMyRecentTasks } from './timer/useMyRecentTasks';
import type { RecentTask } from './timer/timerModel';
import { formatTime } from '../utils/format';
import EngagementFilterFields, { useEngagementFilter } from './engagements/EngagementFilterFields';
import { taskContainerLabel } from '../utils/engagementLabel';
import { approvedMonthsHit, startableTaskAsTicket } from './worklogs/manualWorkLogModel';
import { useBillableBoxDisabled } from './worklogs/useBillableAuthority';
import WorkLogOverlapNotice from './worklogs/WorkLogOverlapNotice';
import { parallelConfirmTimes, shortenedTimes, type ShortenedTimes } from './worklogs/overlapModel';
import DateTimeInput from './ui/DateTimeInput';
import DateInput from './ui/DateInput';
import { isPickableLogDate, timesOnDate, todayLocalDate } from './worklogs/logDateModel';
import { usePermissions } from '../hooks/usePermissions';
import { settleSubmit, useSubmitOnce } from '../hooks/useSubmitOnce';

// Extracted from pages/WorkLogsPage.tsx (T-16a) so QuickCreate can open the same
// manual work-log form from any screen. The form, its queries and its save call
// are unchanged; WorkLogsPage and VoicePage keep using it.

export type NewWorkLogTab = 'tasks' | 'tickets';

/** Manual work-log validation (T-43): both times, end after start, a summary of 5+ characters (as the form always required). */
const WORK_LOG_SCHEMA: FormSchema = {
  'nwl-start': [rules.required()],
  'nwl-end': [rules.required(), rules.after('nwl-start', 'timer.endBeforeStart')],
  'nwl-summary': [rules.required(), rules.minLength(5)],
};

/** A duration the field can read (see `parseDurationInput`). */
const durationRule = (key = 'workLogs.durationField.invalid'): FieldRule => (v) => {
  const s = v == null ? '' : String(v).trim();
  return s && parseDurationInput(s) == null ? { key } : null;
};

/**
 * The quick form (owner 2026-10-11): a duration, ending now. The start / end
 * it fills are still what the server receives; "שינוי שעות" switches to the
 * full start / end fields and WORK_LOG_SCHEMA.
 */
const DURATION_SCHEMA: FormSchema = {
  'nwl-duration': [rules.required(), durationRule()],
  'nwl-summary': [rules.required(), rules.minLength(5)],
};

/**
 * Manual work-log form. `initial` pre-fills it from a DRAFT (e.g. a voice
 * recording): the agent still chooses the ticket and times and confirms —
 * nothing is created without this submit.
 *
 * `initialTab` / `initialItem` (QuickCreate, T-16a) open it on a tab with an
 * item already chosen (the task or ticket on screen, or the running timer's).
 * The user can still go back and pick another one; the save is the same.
 *
 * The task is the unit of work (project → milestone → task), so the form opens
 * on "משימות". P-30c (D-174, QX-28): there is no milestones tab — time is always
 * logged on a task.
 */
export function NewWorkLogModal({ onClose, onSuccess, initial, initialTab, initialItem, fixedDate }: {
  onClose: () => void; onSuccess: () => void; initial?: WorkLogDraftInitial;
  initialTab?: NewWorkLogTab; initialItem?: Ticket | null;
  /** FIX-ADD-LOGS-OTHER-DAYS - the day is fixed (YYYY-MM-DD; "עריכת יום" in attendance). */
  fixedDate?: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const [activeTab, setActiveTab] = useState<NewWorkLogTab>(initial ? 'tickets' : initialTab ?? 'tasks');
  const [ticketSearch, setTicketSearch] = useState('');
  // P-02b (plan §6.6): customer → type → engagement → (milestone) filter the tasks.
  const filter = useEngagementFilter();
  // D-275 — an internal-only employee: no "קריאות" tab, no customer / type / engagement (her tasks + "פרויקט").
  const { internalOnly } = usePermissions();
    const filterKey = JSON.stringify(filter.value);

  // Selected item (task / ticket)
  const [selectedTask, setSelectedTask] = useState<Ticket | null>(
    !initial && initialTab === 'tasks' ? initialItem ?? null : null
  );
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(
    initial?.ticket ? (initial.ticket as unknown as Ticket)
      : initialTab === 'tickets' ? initialItem ?? null : null
  );

  // Work log fields. The end defaults to now and the start follows the
  // duration; start / end stay the values sent to the server.
  const draftMinutes = initial?.durationMinutes ?? null;
  // FIX-ADD-LOGS-OTHER-DAYS: the log's day - today by default, or a past day (never the future).
  const today = todayLocalDate();
  const [logDate, setLogDate] = useState(fixedDate ?? today);
  const pastDay = logDate !== today;
  const initialPast = fixedDate && fixedDate !== today ? timesOnDate(fixedDate, '', '', draftMinutes) : null;
  const [endTime, setEndTime] = useState(() => initialPast?.end ?? toLocalInputValue(new Date()));
  const [startTime, setStartTime] = useState(() => initialPast?.start ?? (draftMinutes ? startFromDuration(toLocalInputValue(new Date()), draftMinutes) : ''));
  const [durationText, setDurationText] = useState(() => (draftMinutes ? formatDurationInput(draftMinutes) : ''));
  // "שינוי שעות": the full start / end fields instead of the duration (always on a past day: it has no "ending now").
  const [showTimes, setShowTimes] = useState(!!initialPast);
  const [summary, setSummary] = useState(initial?.summary ?? '');
  const [billable, setBillable] = useState(true);

  /** A duration typed or picked: the start is the end minus it. */
  const applyDuration = (text: string) => {
    setDurationText(text);
    const minutes = parseDurationInput(text);
    setStartTime(minutes ? startFromDuration(endTime, minutes) : '');
  };

  // Start / end edited directly: keep the duration in step (it is shown again on "חזרה למשך").
  const syncDuration = (start: string, end: string) => {
    const minutes = minutesBetween(start, end);
    if (minutes) setDurationText(formatDurationInput(minutes));
  };
  const handleStartChange = (value: string) => {
    setStartTime(value); syncDuration(value, endTime);
    // The day follows the start typed / picked (a fixed day stays as it is).
    if (!fixedDate && isPickableLogDate(value.slice(0, 10))) setLogDate(value.slice(0, 10));
  };
  const handleEndChange = (value: string) => { setEndTime(value); syncDuration(startTime, value); };
  const durationMinutes = parseDurationInput(durationText);

  const [billableTouched, setBillableTouched] = useState(false);
  const [consumption, setConsumption] = useState<ConsumptionChoice>({});
  const [consumptionBlocked, setConsumptionBlocked] = useState(false);

  const isAdmin = user?.role === 'admin';
  const dateInvalid = !isPickableLogDate(logDate);

  // --- Handler helpers: reset dependent filters when parent changes ---
  const resetFilters = (tab: NewWorkLogTab) => {
    setActiveTab(tab);
    setTicketSearch('');
    setSelectedTask(null);
    setSelectedTicket(null);
  };

  // "המשימות האחרונות שלי" at the top of the picker (data the client already has).
  const recent = useMyRecentTasks(5);
  const pickRecent = (item: RecentTask) => {
    // `organization_name` carries the row's "customer · project" line to the selected-item card.
    const asTicket = { id: item.id, title: item.title, task_type: item.task_type ?? undefined, organization_name: item.meta, ...(item.status ? { status: item.status } : {}) } as Ticket;
    if (item.task_type === 'task') {
      resetFilters('tasks');
      setSelectedTask(asTicket);
    } else {
      resetFilters('tickets');
      setSelectedTicket(asTicket);
    }
  };


  // Tasks tab — P-10d (D-71): ONLY the tasks I am on, open — the same list as
  // the "התחל עבודה" window (GET /timer/tasks); the server refuses any other
  // task (403). Tickets, including the phone-call tickets, live in their own
  // "קריאות" tab, as today (D-47).
  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['timer-tasks', 'new-worklog-tasks', filterKey, ticketSearch],
    queryFn: () => api.getStartableTasks({
      search: ticketSearch || undefined,
      ...filter.ticketParams,
      // P-30c (D-174, QX-26): a completed task of mine may be reported on manually (open month only).
      include_completed: true,
    }),
    enabled: activeTab === 'tasks',
    retry: 1,
  });
  const tasks = (tasksData?.data ?? []).map((tk) => startableTaskAsTicket(tk, { company: t('engagementChain.internalWork'), direct: t('engagementChain.directlyUnderOte') }));
  // D-275 (Q2) — the internal discussions she / he takes part in: a small group under the tasks
  // (an internal-only employee, or "עבודה פנימית OTE" picked with no project).
  const showInternalDiscussions = (internalOnly || filter.companyMode) && !filter.value.engagementId;
  const { data: discData } = useQuery({
    queryKey: ['timer-internal-discussions', 'new-worklog', ticketSearch],
    queryFn: () => api.getInternalDiscussions({ search: ticketSearch || undefined }),
    enabled: activeTab === 'tasks' && showInternalDiscussions,
    retry: 1,
  });
  const discussions = showInternalDiscussions ? (discData?.data ?? []).map((d) => startableTaskAsTicket(d)) : [];

  // Tickets — full list of existing קריאות, filterable by text search.
  const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
    queryKey: ['tickets', 'new-worklog-tickets', ticketSearch],
    queryFn: () => api.getTickets({
      limit: 500,
      task_type: 'ticket',
      search: ticketSearch || undefined,
    }),
    enabled: activeTab === 'tickets',
    retry: 1,
  });
  const tickets = (ticketsData?.data ?? []) as Ticket[];

  // --- Save mutation ---
  const attempt = useSubmitOnce(); // DQ-060: one Idempotency-Key per attempt (the browser lock is DQ-044)
  const mutation = useMutation({
    onSettled: (_d, err) => settleSubmit(attempt, err),
    // D-291: `arg` = the shortened times ("לקצר אוטומטית עד").
    mutationFn: (arg: ShortenedTimes | void) => {
      const override = arg as ShortenedTimes | undefined;
      let ticketId = '';
      if (activeTab === 'tasks' && selectedTask) {
        ticketId = selectedTask.id;
      } else if (activeTab === 'tickets' && selectedTicket) {
        ticketId = selectedTicket.id;
      }
      // Quick form: "ending now" means now at the save, not when the form opened.
      let start = startTime;
      let end = endTime;
      if (!showTimes && durationMinutes) {
        end = toLocalInputValue(new Date());
        start = startFromDuration(end, durationMinutes);
      }
      const payload = {
        ticket_id: ticketId,
        start_time: override?.start_time ?? new Date(start).toISOString(),
        end_time: override?.end_time ?? new Date(end).toISOString(),
        summary,
        // Decision 33: billability resolves from the engagement unless the
        // user deliberately changed it.
        billable: billableTouched ? billable : undefined,
        hour_bank_id: consumption.hour_bank_id,
        override_reason: consumption.override_reason?.trim() || undefined,
        confirm_parallel: override?.confirm_parallel || undefined, // D-299
      };
      // FU-53: a ticket without an engagement asks on which one to log (like "התחלת עבודה").
      return withWorkEngagement({ id: ticketId, title: selectedTitle }, (engagement_id) => (initial?.voiceEntryId
        ? api.createWorkLogFromVoice({ ...payload, engagement_id, voice_entry_id: initial.voiceEntryId })
        : api.createWorkLog({ ...payload, engagement_id }, attempt.key)));
    },
    onSuccess: (res) => {
      if (!res) return; // the engagement question was closed: nothing saved
      toastDebtWarning(res); // D-110
      toastWorkLogNotices(res); // D-266
      queryClient.invalidateQueries({ queryKey: ['work-logs'] });
      if (initial?.voiceEntryId) queryClient.invalidateQueries({ queryKey: ['voice-entries'] });
      onSuccess();
    },
  });

  const selectedAny = !!(selectedTask || selectedTicket);

  // FIX-B11 (QX-26): a COMPLETED task may be reported on only in a month I have
  // not approved — refused here already (message + "שמור" disabled), not only
  // by the server's 409 WORK_LOG_MONTH_APPROVED.
  const selectedCompleted = activeTab === 'tasks' && selectedTask?.status === 'completed';
  const { data: lockedMonthsData } = useQuery({
    queryKey: ['attendance', 'my-locked-months'],
    queryFn: () => api.getMyLockedMonths(),
    enabled: selectedCompleted || pastDay,
    retry: 1,
  });
  const approvedHit = selectedCompleted ? approvedMonthsHit(startTime, endTime, lockedMonthsData?.data ?? []) : [];
  // FIX-ADD-LOGS-OTHER-DAYS (D-319): a past day in a month I already approved (locked) - refused here with a clear message, not only by the server.
  const lockedHit = !selectedCompleted && pastDay ? approvedMonthsHit(startTime, endTime, lockedMonthsData?.data ?? []) : [];
  // D-347 §4: my own logs stay open in an approved month - a notice only; a COMPLETED task keeps its refusal (D-174).
  const monthBlocked = approvedHit.length > 0;
  const selectedTicketId = activeTab === 'tasks' ? selectedTask?.id || '' : selectedTicket?.id || '';
  // BQ-069(a) (D-71): "לחיוב" is disabled for whoever the server would not honour it from.
  const billableLocked = useBillableBoxDisabled(selectedTicketId);

  const renderParentChain = () => {
    if (activeTab === 'tasks' && selectedTask) {
      const parts = [selectedTask.organization_name, taskContainerLabel(selectedTask, t)].filter(Boolean);
      return parts.join(' · ');
    }
    if (activeTab === 'tickets' && selectedTicket) {
      const parts = [selectedTicket.organization_name, taskContainerLabel(selectedTicket, t)].filter(Boolean);
      return parts.join(' · ');
    }
    return '';
  };

  const formId = 'new-worklog-form';
  const isDesktop = useIsDesktop();
  // Desktop: start in the search (as before); a phone sheet focuses its title instead.
  const searchRef = useRef<HTMLInputElement>(null);
  const schema = showTimes ? WORK_LOG_SCHEMA : DURATION_SCHEMA;
  const v = useFormValidation(schema, { 'nwl-duration': durationText, 'nwl-start': startTime, 'nwl-end': endTime, 'nwl-summary': summary });
  const fieldLabels = {
    'nwl-duration': t('workLogs.durationField.label'),
    'nwl-start': t('ticketDetail.startTime'),
    'nwl-end': t('ticketDetail.endTime'),
    'nwl-summary': t('ticketDetail.summary'),
  };
  /** Another day picked: the times move to it (keeping their hours); a past day is entered as start / end. */
  const changeDate = (date: string) => {
    setLogDate(date);
    if (!isPickableLogDate(date)) return;
    const moved = timesOnDate(date, startTime, endTime, durationMinutes);
    if (date !== today) {
      setStartTime(moved.start);
      setEndTime(moved.end);
      if (!showTimes) { v.reset(); setShowTimes(true); }
    } else if (showTimes) {
      setStartTime(moved.start);
      setEndTime(moved.end);
    }
  };
  const toggleTimes = () => {
    v.reset();
    if (showTimes) {
      // Back to the duration: it ends now again.
      const now = toLocalInputValue(new Date());
      setEndTime(now);
      setStartTime(durationMinutes ? startFromDuration(now, durationMinutes) : '');
    }
    setShowTimes((s) => !s);
  };
  const selectedTitle = activeTab === 'tasks' ? selectedTask?.title : selectedTicket?.title;
  const listLoading = activeTab === 'tasks' ? tasksLoading : ticketsLoading;

  /** One row of the pick list: the item, then where it lives. */
  const pickRow = (key: string, icon: ReactNode, title: string, meta: string, onPick: () => void, disabled = false) => (
    <li key={key}>
      <button
        type="button"
        onClick={onPick}
        disabled={disabled}
        className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-start text-sm text-text transition-colors duration-micro hover:bg-surface-subtle disabled:cursor-not-allowed disabled:text-text-disabled disabled:hover:bg-transparent"
      >
        <span className="flex-shrink-0 text-text-3">{icon}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium"><bdi>{title}</bdi></span>
          {meta && <span className="block truncate text-xs text-text-2"><bdi>{meta}</bdi></span>}
        </span>
      </button>
    </li>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('workLogs.newTitle')}
      description={t(internalOnly ? 'workLogs.newSubtitleInternal' : 'workLogs.newSubtitle')}
      size="lg"
      dismissible={!mutation.isPending}
      initialFocus={isDesktop && !selectedAny ? searchRef : undefined}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1 sm:flex-none">{t('common.cancel')}</button>
          {selectedAny && (
            <button
              type="submit"
              form={formId}
              disabled={mutation.isPending || consumptionBlocked || monthBlocked || dateInvalid}
              aria-busy={mutation.isPending || undefined}
              className="btn btn-primary flex-1 sm:flex-none"
            >
              {mutation.isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
              {t('form.saveWorkLog')}
            </button>
          )}
        </>
      )}
    >
        {initial?.voiceEntryId && (
          <div className="mb-3 space-y-0.5 rounded-lg bg-accent-soft px-3 py-2 text-xs text-accent-soft-text">
            <p className="font-medium">{t('voice.draftBanner')}</p>
            {draftMinutes
              ? <p>{t('voice.draftStartHint', { minutes: draftMinutes })}</p>
              : <p>{t('voice.proposalNoDuration')}</p>}
            {initial.statedStart && <p>{t('voice.proposalStatedStart', { value: initial.statedStart })}</p>}
            {initial.statedEnd && <p>{t('voice.proposalStatedEnd', { value: initial.statedEnd })}</p>}
          </div>
        )}

        {/* "המשימות האחרונות שלי": the usual pick in one press, above the full search */}
        {!selectedAny && recent.items.length > 0 && (
          <section className="mb-4" aria-labelledby="nwl-recent-title">
            <h3 id="nwl-recent-title" className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-text-2">
              <History size={14} strokeWidth={1.75} aria-hidden="true" />
              {t('workLogs.recentMine')}
            </h3>
            <ul className="space-y-0.5">
              {recent.items.map((item) => pickRow(
                `recent-${item.id}`,
                item.task_type === 'task'
                  ? <ListChecks size={14} strokeWidth={1.75} aria-hidden="true" />
                  : <FileText size={14} strokeWidth={1.75} aria-hidden="true" />,
                item.title,
                item.meta ?? '',
                () => pickRecent(item),
              ))}
            </ul>
          </section>
        )}

        {/* What the time is logged on (D-275: tasks only for an internal-only employee) */}
        {!internalOnly && <Tabs
          variant="segmented"
          ariaLabel={t('form.workLogTarget')}
          value={activeTab}
          onChange={(k) => resetFilters(k)}
          className="mb-3 w-full [&>button]:flex-1 [&>button]:justify-center"
          items={[
            { key: 'tasks', label: t('timer.tabTasks'), icon: <ListChecks size={14} strokeWidth={1.75} aria-hidden="true" /> },
            { key: 'tickets', label: t('timer.tabTickets'), icon: <FileText size={14} strokeWidth={1.75} aria-hidden="true" /> },
          ]}
        />}

        {/* Filters + list (selection phase) */}
        {!selectedAny && (
          <>
            {activeTab === 'tasks' && (
              <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* P-02b (§6.6): customer → type → engagement → (milestone, project / development) */}
                <EngagementFilterFields filter={filter} idPrefix="nwl" allowAllCustomers={isAdmin} />
              </div>
            )}

            <div className="relative mb-3">
              <Search size={14} strokeWidth={1.75} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-text-3" aria-hidden="true" />
              <input
                ref={searchRef}
                type="search"
                aria-label={t('workLogs.searchPlaceholder')}
                placeholder={t('workLogs.searchPlaceholder')}
                className="input ps-9"
                value={ticketSearch}
                onChange={(e) => setTicketSearch(e.target.value)}
              />
            </div>

            <div className="max-h-64 overflow-y-auto" aria-busy={listLoading || undefined}>
              {listLoading ? (
                <StateBlock kind="loading" size="inline" />
              ) : activeTab === 'tasks' ? (
                tasks.length === 0 && discussions.length === 0 ? <p className="py-4 text-center text-sm text-text-2">{t('timer.noTasksFound')}</p> : (
                  <>
                  <ul className="space-y-0.5">
                    {tasks.map((task) => pickRow(
                      task.id,
                      <ListChecks size={14} strokeWidth={1.75} aria-hidden="true" />,
                      task.title,
                      [task.organization_name, taskContainerLabel(task, t)].filter(Boolean).join(' · '),
                      () => setSelectedTask(task),
                    ))}
                  </ul>
                  {discussions.length > 0 && (
                    <section className="mt-2" aria-labelledby="nwl-discussions-title">
                      <h3 id="nwl-discussions-title" className="mb-1 px-1 text-xs font-semibold text-text-2">{t('timer.internalDiscussionsTitle')}</h3>
                      <ul className="space-y-0.5">
                        {discussions.map((d) => pickRow(
                          d.id,
                          <FileText size={14} strokeWidth={1.75} aria-hidden="true" />,
                          d.title,
                          d.organization_name ?? '',
                          () => setSelectedTask(d),
                        ))}
                      </ul>
                    </section>
                  )}
                  </>
                )
              ) : (
                tickets.length === 0 ? <p className="py-4 text-center text-sm text-text-2">{t('timer.noTicketsFound')}</p> : (
                  <ul className="space-y-0.5">
                    {tickets.map((ticket) => pickRow(
                      ticket.id,
                      <FileText size={14} strokeWidth={1.75} aria-hidden="true" />,
                      ticket.title,
                      [ticket.organization_name, taskContainerLabel(ticket, t)].filter(Boolean).join(' · '),
                      () => setSelectedTicket(ticket),
                    ))}
                  </ul>
                )
              )}
            </div>
          </>
        )}

        {/* Selected item + form */}
        {selectedAny && (
          <div>
            <div className="mb-4 flex items-start justify-between gap-3 rounded-lg bg-surface-subtle px-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate font-semibold text-text"><bdi>{selectedTitle}</bdi></p>
                <p className="truncate text-xs text-text-2"><bdi>{renderParentChain()}</bdi></p>
              </div>
              <button type="button" onClick={resetFilters.bind(null, activeTab)} className="btn btn-ghost btn-sm flex-shrink-0 text-accent-text">
                {t('form.changeItem')}
              </button>
            </div>

            <form id={formId} onSubmit={v.submit(() => mutation.mutate())} noValidate className="space-y-4">
              {v.showSummary && <FormErrorSummary errors={v.errors} labels={fieldLabels} order={Object.keys(schema)} />}
              <Field id="nwl-date" label={t('workLogs.logDate.label')} required className="sm:max-w-xs"
                hint={fixedDate ? undefined : t('workLogs.logDate.hint')}
                error={dateInvalid ? t('workLogs.logDate.invalid') : undefined}>
                <DateInput dir="ltr" className="input text-start" value={logDate} max={today} disabled={!!fixedDate}
                  data-testid="nwl-date" onChange={(e) => changeDate(e.target.value)} />
              </Field>
              {!showTimes ? (
                <div>
                  <Field
                    id="nwl-duration"
                    label={t('workLogs.durationField.label')}
                    required
                    hint={t('workLogs.durationField.hint')}
                    error={v.errors['nwl-duration']}
                  >
                    {(control) => (
                      <div className="flex flex-wrap items-center gap-2">
                        <div role="group" aria-label={t('workLogs.durationField.presets')} className="flex flex-wrap gap-1.5">
                          {DURATION_PRESETS.map((m) => {
                            const active = durationMinutes === m;
                            return (
                              <button
                                key={m}
                                type="button"
                                aria-pressed={active}
                                onClick={() => { applyDuration(formatDurationInput(m)); v.blur('nwl-duration'); }}
                                className={`btn btn-sm rounded-full tabular-nums ${active ? 'border border-accent bg-accent-soft text-accent-soft-text' : 'btn-secondary'}`}
                              >
                                {m < 60
                                  ? t('workLogs.durationField.presetMinutes', { value: m })
                                  : t('workLogs.durationField.presetHours', { value: m / 60 })}
                              </button>
                            );
                          })}
                        </div>
                        <input
                          {...control}
                          type="text"
                          inputMode="decimal"
                          dir="ltr"
                          autoComplete="off"
                          placeholder="1:30"
                          className={`input w-24 text-center tabular-nums ${v.errors['nwl-duration'] ? 'input-error' : ''}`}
                          value={durationText}
                          onChange={(e) => applyDuration(e.target.value)}
                          onBlur={() => {
                            const minutes = parseDurationInput(durationText);
                            if (minutes) setDurationText(formatDurationInput(minutes));
                            v.blur('nwl-duration');
                          }}
                        />
                      </div>
                    )}
                  </Field>
                  <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-text-2">
                    <span className="tabular-nums">
                      {durationMinutes && startTime
                        ? t('workLogs.durationField.range', { start: formatTime(startTime), end: formatTime(endTime) })
                        : t('workLogs.durationField.endsNow')}
                    </span>
                    <button type="button" onClick={toggleTimes} className="font-medium text-accent-text hover:underline">
                      {t('workLogs.durationField.changeTimes')}
                    </button>
                  </p>
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field id="nwl-start" label={t('ticketDetail.startTime')} required error={v.errors['nwl-start']}>
                      <DateTimeInput dir="ltr" className="input text-start" value={startTime} max={toLocalInputValue(new Date())}
                        onChange={(e) => handleStartChange(e.target.value)} onBlur={() => v.blur('nwl-start')} />
                    </Field>
                    <Field id="nwl-end" label={t('ticketDetail.endTime')} required hint={t('workLogs.manual.noFutureHint')} error={v.errors['nwl-end']}>
                      <DateTimeInput dir="ltr" className="input text-start" value={endTime} max={toLocalInputValue(new Date())}
                        onChange={(e) => handleEndChange(e.target.value)} onBlur={() => v.blur('nwl-end')} />
                    </Field>
                  </div>
                  <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-text-2">
                    {minutesBetween(startTime, endTime) && (
                      <span className="tabular-nums">
                        {t('workLogs.durationField.total', { value: formatDurationInput(minutesBetween(startTime, endTime) ?? 0) })}
                      </span>
                    )}
                    {!pastDay && (
                      <button type="button" onClick={toggleTimes} className="font-medium text-accent-text hover:underline">
                        {t('workLogs.durationField.backToDuration')}
                      </button>
                    )}
                  </p>
                </div>
              )}
              <Field id="nwl-summary" label={t('ticketDetail.summary')} required hint={t('form.summaryHint')} error={v.errors['nwl-summary']}>
                <textarea className="input min-h-24" value={summary} onChange={(e) => setSummary(e.target.value)} onBlur={() => v.blur('nwl-summary')} rows={4} />
              </Field>
              <label htmlFor="billable-new" className={`inline-flex items-center gap-2 text-sm text-text ${billableLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                title={billableLocked ? t('workLogs.billableLocked') : undefined}>
                <input type="checkbox" id="billable-new" checked={billable} disabled={billableLocked} onChange={(e) => { setBillable(e.target.checked); setBillableTouched(true); }} className="h-4 w-4 accent-primary-600" />
                {t('common.billable')}
              </label>
              {selectedTicketId && (
                <ConsumptionPreview
                  ticketId={selectedTicketId}
                  startTime={startTime}
                  endTime={endTime}
                  billable={billableTouched ? billable : undefined}
                  value={consumption}
                  onChange={setConsumption}
                  onBlockedChange={setConsumptionBlocked}
                  onAutoBillable={(b) => { if (!billableTouched) setBillable(b); }}
                />
              )}
              {monthBlocked && (
                <p role="alert" className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
                  {t('workLogs.manual.monthApproved', { months: approvedHit.join(', ') })}
                </p>
              )}
              {!monthBlocked && lockedHit.length > 0 && (
                <p role="status" className="rounded-lg border border-border bg-surface-subtle px-3 py-2 text-sm text-text-2" data-testid="nwl-month-locked-notice">
                  {t('workLogs.manual.monthLocked', { months: lockedHit.join(', ') })}
                </p>
              )}
              <WorkLogOverlapNotice
                error={mutation.error}
                fallback={t('workLogs.createError')}
                busy={mutation.isPending}
                onShorten={(c) => { const times = shortenedTimes(c); if (times) mutation.mutate(times); }}
                onConfirmParallel={(c) => { const times = parallelConfirmTimes(c); if (times) mutation.mutate(times); }}
                onRetry={() => mutation.mutate(mutation.variables)}
              />
            </form>
          </div>
        )}
    </Dialog>
  );
}

